import React from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { INPUT_BASE_CLASSES, COUNTRY_CODES, formatPhoneForDisplay, extractDigits, cx } from './formStyles';

type PhoneInputProps = {
  id: string;
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (code: string) => void;
  onPhoneNumberChange: (phone: string) => void;
  error?: string;
};

export function PhoneInput({
  id,
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  error
}: PhoneInputProps) {
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = extractDigits(e.target.value);
    const maxDigits = countryCode === '+1' ? 10 : 15;
    onPhoneNumberChange(digits.slice(0, maxDigits));
  };

  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex gap-3">
        <select
          id={`${id}-country-code`}
          value={countryCode}
          onChange={(e) => onCountryCodeChange(e.target.value)}
          className={cx(
            'w-[110px] sm:w-[220px] rounded-xl border border-onboarding-primary/30 bg-onboarding-content-bg px-3 sm:px-4 py-[18px] text-sm sm:text-base text-onboarding-text-dark transition focus:border-onboarding-primary focus:outline-none',
            error && 'border-red-600'
          )}
        >
          {COUNTRY_CODES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.label}
            </option>
          ))}
        </select>
        <div className="relative flex-1">
          <input
            id={id}
            type="tel"
            inputMode="tel"
            value={formatPhoneForDisplay(phoneNumber, countryCode)}
            onChange={handlePhoneChange}
            placeholder={countryCode === '+1' ? '(555) 123-4567' : 'Phone number'}
            className={cx(
              INPUT_BASE_CLASSES,
              "font-['Helvetica Neue','Arial',sans-serif]",
              'placeholder:text-[#707C8B]',
              error && 'border-red-600 pr-12'
            )}
          />
          {error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
              <ExclamationCircleIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
            </div>
          )}
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
